/**
 * Felicita (Arc, Parallel, Incline) wire format: pure encoding and decoding, no Web Bluetooth. Reverse-engineered by
 * other projects, never published by Felicita. See docs/felicita-protocol.md for the sources, where they disagree,
 * and the reasoning behind each choice.
 */

/** A generic BLE-to-UART service: plenty of devices that aren't scales use it too. */
export const FELICITA_SERVICE_UUID = '0000ffe0-0000-1000-8000-00805f9b34fb';
/** The one characteristic, for frames from the scale (notify) and commands to it (write). */
export const FELICITA_CHAR_UUID = '0000ffe1-0000-1000-8000-00805f9b34fb';

/** The advertised name, as every source shows it (Web Bluetooth name prefixes are case-sensitive). */
export const FELICITA_NAME_PREFIX = 'FELICITA';

/** Case-insensitive prefix, ignoring surrounding spaces. */
export function isFelicitaName(name: string | null | undefined): boolean {
  return !!name && name.trim().toUpperCase().startsWith(FELICITA_NAME_PREFIX);
}

/** One ASCII byte per command. */
export const FelicitaCommand = {
  tare: 0x54, // 'T'
  startTimer: 0x52, // 'R'
  stopTimer: 0x53, // 'S'
  resetTimer: 0x43, // 'C'
} as const;

/** A command is exactly one byte: a longer buffer would reach the scale as more commands. */
export function encodeCommand(command: number): Uint8Array<ArrayBuffer> {
  if (!Number.isInteger(command) || command < 0 || command > 0xff) throw new RangeError(`Felicita command byte out of range: ${command}`);
  return new Uint8Array([command]);
}

export const felicitaCommands = {
  tare: () => encodeCommand(FelicitaCommand.tare),
  startTimer: () => encodeCommand(FelicitaCommand.startTimer),
  stopTimer: () => encodeCommand(FelicitaCommand.stopTimer),
  resetTimer: () => encodeCommand(FelicitaCommand.resetTimer),
};

/** Every source's frame length. Others are still read (see parseFelicitaFrame), and counted. */
export const FELICITA_FRAME_LENGTH = 18;
/** Header, sign and the six weight digits. */
export const FELICITA_MIN_FRAME_LENGTH = 9;

/** Raw battery byte (15) for empty and full: most sources agree; pyfelicita uses 137-161. */
export const FELICITA_BATTERY_EMPTY = 129;
export const FELICITA_BATTERY_FULL = 158;

export const GRAMS_PER_OUNCE = 28.349523125;

const ASCII_0 = 0x30;
const ASCII_MINUS = 0x2d;
const ASCII_PLUS = 0x2b;

export type FelicitaUnit = 'g' | 'oz' | 'unknown';

export interface FelicitaFrame {
  /** Always grams, converted when the scale displays ounces. -0 is reported as 0. */
  weightG: number;
  /** The unit the scale displays (bytes 9-10). 'unknown' is read as grams. */
  unit: FelicitaUnit;
  /** 0-100 from byte 15, or null when the frame is too short to carry it. */
  batteryPct: number | null;
  batteryRaw: number | null;
  /** Bytes 0-1 are 01 02 and byte 2 is '+' or '-'. Diagnostics only: frames are used either way. */
  headerOk: boolean;
  /** The frame is the usual 18 bytes. Diagnostics only. */
  lengthOk: boolean;
}

export type FelicitaParseResult = { ok: true; frame: FelicitaFrame } | { ok: false; reason: 'length' | 'digits' };

/**
 * Decode one notification. Fewer than 9 bytes, or a weight field that isn't six ASCII digits, is malformed. The header
 * and the length are reported, never enforced (no source says the Parallel or Incline send the same header).
 */
export function parseFelicitaFrame(bytes: Uint8Array): FelicitaParseResult {
  if (bytes.length < FELICITA_MIN_FRAME_LENGTH) return { ok: false, reason: 'length' };
  let digits = 0;
  for (let i = 3; i <= 8; i++) {
    const d = bytes[i]! - ASCII_0;
    if (d < 0 || d > 9) return { ok: false, reason: 'digits' };
    digits = digits * 10 + d;
  }
  const sign = bytes[2]!;
  const negative = sign === ASCII_MINUS && digits !== 0;
  const unit = bytes.length >= 11 ? felicitaUnit(bytes[9]!, bytes[10]!) : 'unknown';
  const native = unit === 'oz' ? (digits / 10_000) * GRAMS_PER_OUNCE : digits / 100;
  const magnitude = unit === 'oz' ? Math.round(native * 100) / 100 : native;
  const batteryRaw = bytes.length > 15 ? bytes[15]! : null;
  return {
    ok: true,
    frame: {
      weightG: negative ? -magnitude : magnitude,
      unit,
      batteryPct: batteryRaw === null ? null : felicitaBatteryPct(batteryRaw),
      batteryRaw,
      headerOk: bytes[0] === 0x01 && bytes[1] === 0x02 && (sign === ASCII_PLUS || sign === ASCII_MINUS),
      lengthOk: bytes.length === FELICITA_FRAME_LENGTH,
    },
  };
}

/** Bytes 9-10 as ASCII, without spaces and NULs: " g" and "g\0" are grams, "oz" is ounces. */
export function felicitaUnit(b9: number, b10: number): FelicitaUnit {
  const text = String.fromCharCode(b9, b10).replace(/[\s\0]/g, '').toLowerCase();
  if (text === 'g') return 'g';
  if (text === 'oz') return 'oz';
  return 'unknown';
}

/** round((raw - 129) / 29 x 100), clamped to 0-100. */
export function felicitaBatteryPct(raw: number): number {
  const pct = Math.round(((raw - FELICITA_BATTERY_EMPTY) / (FELICITA_BATTERY_FULL - FELICITA_BATTERY_EMPTY)) * 100);
  return Math.max(0, Math.min(100, pct));
}

/** "01 02 2b 30 …" */
export function felicitaHex(bytes: ArrayLike<number>): string {
  return Array.from({ length: bytes.length }, (_, i) => ((bytes[i] ?? 0) & 0xff).toString(16).padStart(2, '0')).join(' ');
}

/**
 * A frame in the layout every source describes, for tests and docs: 01 02, sign, six digits, unit, three unknown
 * bytes ("SO0", from Beanconqueror's sample), beep flag, battery, CR LF. `value` is in the unit's own steps
 * (hundredths of a gram, ten-thousandths of an ounce).
 */
export function encodeFelicitaFrame(value: number, opts: { unit?: 'g' | 'oz'; batteryRaw?: number; sign?: '+' | '-' } = {}): Uint8Array {
  const digits = String(Math.abs(Math.round(value))).padStart(6, '0');
  if (digits.length > 6) throw new RangeError(`Felicita weight out of range: ${value}`);
  const sign = opts.sign ?? (value < 0 ? '-' : '+');
  const unit = opts.unit === 'oz' ? 'oz' : ' g';
  const text = `${sign}${digits}${unit}SO0`;
  return new Uint8Array([0x01, 0x02, ...Array.from(text, (c) => c.charCodeAt(0)), 0x22, opts.batteryRaw ?? 0x89, 0x0d, 0x0a]);
}
